import * as R from 'ramda'
import api from '@/api'
import uuidv4 from '@/utilities/uuid.js'
import { successPipeline } from '@/api/pipelines/success-pipeline.js'

const customRateItemPresenter = (customRateItem, itemId) => {
  return {
    id: itemId,
    description: customRateItem['description'],
    cost: customRateItem['cost'],
    defaultQuantity: customRateItem['defaultQuantity']
  }
}

const state = {
  customRateItems: {}
}

const mutations = {
  LOAD_CUSTOM_RATE_ITEMS (state, payload) {
    state.customRateItems = payload
  },

  ADD_CUSTOM_RATE_ITEM (state, { id, item }) {
    const items = {
      ...state.customRateItems,
      [id]: item
    }
    state.customRateItems = items
  },

  UPDATE_CUSTOM_RATE_ITEM (state, { id, item }) {
    state.customRateItems[id] = item
  },

  DELETE_CUSTOM_RATE_ITEM (state, payload) {
    state.customRateItems = R.omit(payload, state.customRateItems)
  },

  API_FAILURE (state, payload) {
    state.errorMessage = payload
  }
}

const actions = {
  updateCustomRateItem ({ commit, getters }, jsConn) {
    if (jsConn.formParams.id === 'new') {
      const id = uuidv4()
      jsConn.formParams = {
        id: id,
        item: jsConn.formParams.item
      }

      if (getters.signedIn) {
        return api.createCustomRate(jsConn)
          .then(jsConn => {
            // run it through the success pipeline
            return successPipeline(jsConn)
          })
          .then(jsConn => {
            jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully saved'
            commit('ADD_CUSTOM_RATE_ITEM', jsConn.formParams)
            return Promise.resolve(jsConn)
          })
          .catch(jsConn => {
            jsConn.toastMessage = 'Unable to save ' + jsConn.formParams['item']['description']
            return Promise.reject(jsConn)
          })
      } else {
        commit('ADD_CUSTOM_RATE_ITEM', jsConn.formParams)
        jsConn.toastMessage = jsConn.formParams['item']['description'] + ' saved for this browser session'
        return Promise.resolve(jsConn)
      }
    } else {
      if (getters.signedIn) {
        return api.updateCustomRate(jsConn)
          .then(jsConn => {
            return successPipeline(jsConn)
          })
          .then(jsConn => {
            commit('UPDATE_CUSTOM_RATE_ITEM', jsConn.formParams)
            jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully updated'
            return Promise.resolve(jsConn)
          })
          .catch(jsConn => {
            jsConn.toastMessage = 'Unable to save ' + jsConn.apiParams['item']['description']
            return Promise.reject(jsConn)
          })
      } else {
        commit('UPDATE_CUSTOM_RATE_ITEM', jsConn.formParams)
        jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully updated for this browser session'
        return Promise.resolve(jsConn)
      }
    }
  },

  deleteCustomRateItem ({ commit, getters, dispatch }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCustomRate(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          // delete it from any crops
          dispatch('deleteCustomRateItemFromAllCrops', jsConn)
          commit('DELETE_CUSTOM_RATE_ITEM', [jsConn.formParams['id']])
          jsConn.toastMessage = 'Custom rate item deleted'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to delete item'
          return Promise.reject(jsConn)
        })
    } else {
      dispatch('deleteCustomRateItemFromAllCrops', jsConn)
      commit('DELETE_CUSTOM_RATE_ITEM', [jsConn.formParams['id']])
      jsConn.toastMessage = 'Item deleted for this browser session'
      return Promise.resolve(jsConn)
    }
  }
}

const getters = {
  customRateItems: state => state.customRateItems,

  customRateItemsIdList: state => {
    return Object.keys(state.customRateItems)
  },

  customRateItemsList: (state, getters) => {
    const itemsList = R.map((itemId) => {
      const item = getters.getCustomRateItemById(itemId)
      return customRateItemPresenter(item, itemId)
    }, getters.customRateItemsIdList)

    return itemsList
  },

  getCustomRateItemById: (state) => (id) => {
    return state.customRateItems[id]
  }
}

const customRateModule = {
  state,
  mutations,
  actions,
  getters
}

export default customRateModule
